import React, { useRef, useState } from "react";
import { Formik, FormikHelpers } from "formik";
import * as yup from "yup";
import ReCAPTCHA from "react-google-recaptcha";
import { Spinner } from "react-bootstrap";
import { useTranslation } from "next-export-i18n";

import { TextInputField } from "../components/formik/text-input";
import { SelectField } from "../components/formik/select";
import { postItems } from "../services";

const phoneRegExp =
    /^((\+[1-9]{1,4}[ -]?)|(\([0-9]{2,3}\)[ -]?)|([0-9]{2,4})[ -]?)*?[0-9]{3,4}[ -]?[0-9]{3,4}$/;

const recaptchaKey = process.env.NEXT_PUBLIC_RECAPTCHA_KEY || "";

const ContactForm = () => {
    const [contactMsg, setContactMsg] = useState("");
    const [recaptchaNeeded, setRecaptchaNeeded] = useState(false);
    const recaptchaRef = useRef<any>();
    const { t } = useTranslation();

    const companyTypes = [
        {
            value: "ad_agency",
            label: t("contact.form.fields.company_type.options.ad_agency"),
        },
        {
            value: "reseller",
            label: t("contact.form.fields.company_type.options.reseller"),
        },
        {
            value: "other",
            label: t("contact.form.fields.company_type.options.other"),
        },
    ];

    const validationSchema = yup.object().shape({
        fullName: yup
            .string()
            .required(t("contact.form.validation_error.required")),
        email: yup
            .string()
            .email(t("contact.form.validation_error.email"))
            .required(t("contact.form.validation_error.required")),
        companyType: yup
            .string()
            .required(t("contact.form.validation_error.required")),
        phone: yup
            .string()
            .matches(phoneRegExp, t("contact.form.validation_error.phone")),
    });

    const onSubmit = async (values: any, formikHelpers: FormikHelpers<any>) => {
        const { fullName, email, companyType, phone, message } = values;

        try {
            formikHelpers.setSubmitting(true);
            const captcha = await recaptchaRef.current!.executeAsync();
            await postItems("/homepage/contact_us", {
                name: fullName,
                email,
                companyType,
                message,
                phone,
                captcha,
            });

            setContactMsg(t("contact.form.message"));
            formikHelpers.setFieldValue("fullName", "");
            formikHelpers.setFieldValue("email", "");
            formikHelpers.setFieldValue("companyType", "");
            formikHelpers.setFieldValue("phone", "");
            formikHelpers.setFieldValue("message", "");
            formikHelpers.setSubmitting(false);
        } catch (error: any) {
            formikHelpers.setStatus("Request error!");
            formikHelpers.setSubmitting(false);
        }
    };

    const handleChange = () => {
        if (!recaptchaNeeded) {
            setRecaptchaNeeded(true);
        }
    };

    return (
        <Formik
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            initialValues={{
                fullName: "",
                email: "",
                companyType: "",
                phone: "",
                message: "",
            }}
            enableReinitialize={true}
        >
            {({ handleSubmit, isSubmitting, status }: any) =>
                contactMsg ? (
                    <div className="mt-40 text-center">{contactMsg}</div>
                ) : (
                    <>
                        <p>{t("contact.form.subtitle")}</p>
                        <div className="row tp-gx-10">
                            <div className="col-md-12">
                                <div className="tp-contact-input">
                                    <TextInputField
                                        name="fullName"
                                        placeholder={t(
                                            "contact.form.fields.full_name"
                                        )}
                                        isRequired={true}
                                        disabled={isSubmitting}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="tp-contact-input">
                                    <TextInputField
                                        name="email"
                                        placeholder={t(
                                            "contact.form.fields.email"
                                        )}
                                        type="email"
                                        isRequired={true}
                                        disabled={isSubmitting}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                            <div className="col-md-6 company-type">
                                <div className="tp-contact-input">
                                    <SelectField
                                        name="companyType"
                                        placeholder={t(
                                            "contact.form.fields.company_type.title"
                                        )}
                                        options={companyTypes}
                                        isRequired={true}
                                        disabled={isSubmitting}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                            <div className="col-md-12">
                                <div className="tp-contact-input">
                                    <TextInputField
                                        name="phone"
                                        placeholder={t(
                                            "contact.form.fields.phone"
                                        )}
                                        disabled={isSubmitting}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                            <div className="col-md-12">
                                <div className="tp-contact-input">
                                    <TextInputField
                                        name="message"
                                        placeholder={t(
                                            "contact.form.fields.message"
                                        )}
                                        inputProps={{
                                            as: "textarea",
                                        }}
                                        disabled={isSubmitting}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                            <div className="small captcha-msg">
                                This site is protected by reCAPTCHA. reCAPTCHA
                                is protected by the Google{" "}
                                <a
                                    href="https://policies.google.com/privacy"
                                    target="_blank"
                                >
                                    <b>Privacy Policy</b>
                                </a>{" "}
                                and{" "}
                                <a
                                    href="https://policies.google.com/terms"
                                    target="_blank"
                                >
                                    <b>Terms of use</b>
                                </a>
                                .
                            </div>
                            {recaptchaNeeded && (
                                <ReCAPTCHA
                                    // @ts-ignore
                                    ref={recaptchaRef}
                                    sitekey={recaptchaKey}
                                    size="invisible"
                                />
                            )}
                            <div className="tp-contact-btn mt-20">
                                <button
                                    onClick={handleSubmit}
                                    className="tp-btn float-end send"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting && (
                                        <Spinner animation="grow" />
                                    )}
                                    {t("contact.form.btn")}
                                </button>
                            </div>
                        </div>
                        {status && (
                            <div className="mt-40 text-center text-danger">
                                {status}
                            </div>
                        )}
                    </>
                )
            }
        </Formik>
    );
};

export default ContactForm;
